import { defineParts, defineRecipe } from "@pandacss/dev";

const SIDEBAR_HEADER_SELECTOR = '& [data-part="sidebar-header"]';
const SECTION_HEADER_LABEL_SELECTOR = '& [data-part="section-title"]';
const SECTION_ITEM_LABEL =
  '& [data-part="section-title"], & [data-part="item-text"]';
const SECTION_HEADER_SELECTOR =
  '& [data-scope="collapsible"][data-part="trigger"], & [data-scope="segment-group"][data-part="item"]:has([data-part="section-title"])';
const SECTION_CONTENT_SELECTOR =
  '& [data-scope="collapsible"][data-part="content"]';
const SECTION_ITEM_SELECTOR =
  '& [data-scope="segment-group"][data-part="item"]:not(:has([data-part="section-title"]))';
const INDICATOR_SELECTOR =
  '& [data-scope="segment-group"][data-part="indicator"]';
const OPENED_COLLAPSIBLE_CONTENT =
  "& [data-scope='collapsible'][data-part='content'][data-state='open']";
const CLOSED_COLLAPSIBLE_CONTENT =
  "& [data-scope='collapsible'][data-part='content'][data-state='closed']";

const parts = defineParts({
  root: {
    selector: "&",
  },
  sidebarHeader: {
    selector: SIDEBAR_HEADER_SELECTOR,
  },
  sectionHeader: {
    selector: SECTION_HEADER_SELECTOR,
  },
  sectionItem: {
    selector: SECTION_ITEM_SELECTOR,
  },
  indicator: {
    selector: INDICATOR_SELECTOR,
  },
  openedCollapsibleContent: {
    selector: OPENED_COLLAPSIBLE_CONTENT,
  },
  closedCollapsibleContent: {
    selector: CLOSED_COLLAPSIBLE_CONTENT,
  },
});

const SIDEBAR_ITEM_MARGIN = "28px";

export const aromaSidebar = defineRecipe({
  className: "aroma-sidebar",
  jsx: ["Sidebar"],
  base: parts({
    root: {
      minHeight: "100%",
      backgroundColor: "background.neutral.base.subtlest",
      color: "content.neutral.base.subtle",
      transition: "width 0.3s ease",
      _open: {
        width: "width.2xs",
      },
      _closed: {
        width: "64px",
        [`${SECTION_HEADER_LABEL_SELECTOR}`]: {
          display: "none",
        },
        [`${SECTION_HEADER_SELECTOR}`]: {
          px: "unset",
          justifyContent: "center",
        },
        [`${SECTION_CONTENT_SELECTOR}`]: {
          display: "none!",
        },
      },
      borderBottomRightRadius: "2xl",
      boxShadow: "effect.shadow.subtle-bottom",
      containerName: "sidebar",
      [SECTION_ITEM_LABEL]: {
        textOverflow: "ellipsis",
        overflow: "hidden",
      },
      [`${SECTION_HEADER_LABEL_SELECTOR}`]: {
        textWrap: "nowrap",
      },
      xsDown: {
        position: "absolute",
        left: "0",
        top: "0",
        zIndex: "3001",
        transition: "all 0.3s ease",
      },
    },
    sidebarHeader: {
      width: "100%",
      gap: "gap.2",
      borderRadius: "l",
      cursor: "pointer",
      _hover: {
        backgroundColor: "background.neutral.base.subtlest-hover",
      },

      "& span": {
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden",
      },
    },
    sectionHeader: {
      display: "flex",
      width: "100%",
      height: "xl",
      minHeight: "xl",
      minWidth: "xl",
      gap: "gap.2",
      px: "padding.3",
      borderRadius: "l",
      borderWidth: "m",
      borderStyle: "solid",
      borderColor: "transparent",
      zIndex: "5",
      _aromaticonChildren: {
        fontSize: "{sizes.xs}",
      },
      alignItems: "center",
      cursor: "pointer",
      _notDisabled: {
        _hover: {
          bgColor: "background.neutral.base.subtlest-hover",
        },
      },
      _focusVisible: {
        borderWidth: "m",
        borderStyle: "solid",
        borderColor: "border.brand-bold",
        shadow: "effect.focus-ring.brand",
      },
      _open: {
        color: "content.brand.bold",
        borderColor: "border.brand-bold",
        _notDisabled: {
          _hover: {
            bgColor: "background.brand.subtle-hover",
          },
        },
        _checked: {
          borderColor: "transparent",
        },
      },
      _active: {
        color: "content.brand.bold",
        bgColor: "background.brand.subtle",
        _notDisabled: {
          _hover: {
            bgColor: "background.brand.subtle-hover",
          },
        },
      },
      _checked: {
        color: "content.brand.bold",
        bgColor: "background.brand.subtle",
        _notDisabled: {
          _hover: {
            bgColor: "background.brand.subtle-hover",
          },
        },
      },
      _disabled: {
        color: "content.neutral.base.disable",
        bgColor: "unset",
      },
      "& i": {
        textStyle: "title.l",
      },
    },
    sectionItem: {
      textStyle: "paragraph.m.regular",
      wordBreak: "keep-all",
      textWrap: "nowrap",
      height: "xl",
      px: "padding.3",
      ml: SIDEBAR_ITEM_MARGIN,
      display: "flex",
      alignItems: "center",
      gap: "gap.2",
      cursor: "pointer",
      borderRadius: "l",
      position: "relative",
      borderWidth: "m",
      borderStyle: "solid",
      borderColor: "transparent",
      zIndex: "1",
      _hover: {
        bgColor: "background.neutral.base.subtlest-hover",
        zIndex: "2",
      },
      // :focus-visible pseudo-class is set on a child hidden radio input
      "&:has(:focus-visible), &:focus-visible": {
        outline: "none",
        borderColor: "border.brand-bold",
        zIndex: "3",
        shadow: "effect.focus-ring.brand",
      },
      _checked: {
        textStyle: "paragraph.m.bold",
        color: "content.neutral.base.bold",
      },
      _disabled: {
        color: "content.neutral.base.disable",
      },
      _before: {
        content: "''",
        position: "absolute",
        left: "-padding.2",
        width: "{borderWidths.2xl}",
        top: "calc( -1 * {borderWidths.m})",
        bottom: "calc( -1 * {borderWidths.m})",
        marginLeft: "calc( -1 * {borderWidths.m})",
        marginRight: "calc( -1 * {borderWidths.m})",
        bgColor: "border.neutral.subtle",
      },
      _firstOfType: {
        _before: {
          top: "padding.2",
          borderTopRadius: "l",
        },
      },
      _lastOfType: {
        _before: {
          bottom: "padding.2",
          borderBottomRadius: "l",
        },
      },
    },
    indicator: {
      "--transition-duration": ".2s",
      height: "s",
      width: "{borderWidths.2xl}",
      marginLeft: `calc(${SIDEBAR_ITEM_MARGIN} - {spacing.padding.2})`,
      marginTop: "padding.2",
      bgColor: "border.brand-bold",
      borderRadius: "l",
      zIndex: "4",
      "&[data-hidden]": {
        display: "none",
      },
    },
  }),
});
